import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from '@reach/router';
import Logo from '../svg/logo';
import { useStaticQuery, graphql } from 'gatsby';
import CustomLink from './link';
import { gsap } from 'gsap';
import NavBookingsOverlay from './bookings-nav-overlay';

const Header = () => {
	const location = useLocation();

	useEffect(() => {
		setOverlayActive(false);
		setIsActive(false);

		gsap.to(clipPathRef.current, {
			duration: 0.4,
			ease: 'ease-out',
			attr: { d: oldPath },
		});
		gsap.to(preClipRef.current, {
			duration: 0.4,
			ease: 'ease-out',
			attr: { d: oldPath },
		});
	}, [location.pathname]);

	const header = useStaticQuery(graphql`
		{
			header: datoCmsHeaderNavigation {
				textMenu
				textClose
				textBookingMobile
				textBooking
				navLinksQuick {
					id: originalId
					url
					text
				}
				navLinksKey {
					id: originalId
					text
					url
					emoji {
						spin
						image {
							alt
							url(imgixParams: { w: "80" })
						}
					}
				}
				navLinksSecondary {
					id: originalId
					url
					text
					theme
				}
			}
		}
	`);
	const data = header.header;

	const [isActive, setIsActive] = useState(false);
	const [overlayActive, setOverlayActive] = useState(false);

	const handleClick = (event) => {
		setIsActive(!isActive);
	};

	const navOverlayRef = useRef(null);
	const clipPathRef = useRef(null);
	const preClipRef = useRef(null);
	const oldPath = 'M 0 0 V 0 c 0 0 0 0 1 0 V 0 H 0';
	const newPath = 'M 0 0 V 1 c 1 0.5 1 0.25 1 0 V 0 H 0';

	const revealNav = () => {
		handleClick();
		gsap.to(clipPathRef.current, {
			duration: 2,
			ease: 'ease-out',
			attr: { d: newPath },
		});
		gsap.to(preClipRef.current, {
			duration: 1,
			ease: 'ease-out',
			attr: { d: newPath },
		});
	};

	const hideNav = () => {
		handleClick();
		gsap.to(clipPathRef.current, {
			duration: 1.1,
			ease: 'ease-out',
			attr: { d: oldPath },
		});
		gsap.to(preClipRef.current, {
			duration: 1.6,
			ease: 'ease-out',
			attr: { d: oldPath },
		});
	};

	const toggleBookings = (event) => {
		event.preventDefault();
		setOverlayActive(!overlayActive);
	};

	const navLinksLeft =
		data.navLinksQuick.length > 2
			? data.navLinksQuick.slice(0, 2)
			: data.navLinksQuick.slice(0, 1);
	const navLinksRight =
		data.navLinksQuick.length > 2
			? data.navLinksQuick.slice(2, 4)
			: data.navLinksQuick.slice(1, 2);

	return (
		<>
			<header className='navigation'>
				<div className='header-items'>
					<a
						className={`quick-link nav-link-text bookings-link ${
							overlayActive === true && 'is-active'
						}`}
						href='/bookings'
						onClick={toggleBookings}
					>
						<span className='text desktop-text'>
							{data.textBooking}
						</span>
						<span className='text mobile-text'>
							{data.textBookingMobile}
						</span>
						<span className='bookings-plus'>
							<span />
						</span>
					</a>
					<NavBookingsOverlay
						isActive={overlayActive}
						toggleBookings={toggleBookings}
					/>
					{navLinksLeft.map((link, index) => (
						<CustomLink
							classes={`quick-link nav-link-text desktop-link ${
								navLinksRight.length === 2
									? index === 1 && 'desktop-link--large'
									: ''
							}`}
							url={link.url}
							key={`quick-link-${link.id}`}
						>
							<span className='text'>{link.text}</span>
						</CustomLink>
					))}
				</div>
				<CustomLink classes='logo-container' url='/'>
					<Logo />
				</CustomLink>
				<div className='header-items'>
					{navLinksRight.map((link) => (
						<CustomLink
							classes={`quick-link nav-link-text desktop-link ${
								navLinksRight.length === 2 &&
								'desktop-link--large'
							}`}
							url={link.url}
							key={`desktop-link-${link.id}`}
						>
							<span className='text'>{link.text}</span>
						</CustomLink>
					))}
					{navLinksRight.length === 2 && (
						<CustomLink
							classes='quick-link nav-link-text desktop-link desktop-link--not-large'
							url={navLinksLeft[1].url}
						>
							<span className='text'>{navLinksLeft[1].text}</span>
						</CustomLink>
					)}
					<a
						onClick={revealNav}
						className='quick-link nav-link-text nav-btn js-nav-btn'
						href='#nav'
					>
						<span className='text'>{data.textMenu}</span>
						<div className='burger'>
							<span></span>
						</div>
					</a>
				</div>
			</header>
			<div className='pre-clip-container'>
				<svg className='pre-clip' width='0px' height='0px'>
					<defs>
						<clipPath
							id='pre-clip'
							clipPathUnits='objectBoundingBox'
						>
							<path ref={preClipRef} d={oldPath} />
						</clipPath>
					</defs>
				</svg>
			</div>
			<nav
				ref={navOverlayRef}
				className={`nav-overlay js-nav-overlay ${
					isActive ? 'is-active' : ''
				}`}
			>
				<svg className='clip-path' width='0px' height='0px'>
					<defs>
						<clipPath
							id='overlay-clip'
							clipPathUnits='objectBoundingBox'
						>
							<path ref={clipPathRef} d={oldPath} />
						</clipPath>
					</defs>
				</svg>
				<div className='top-items'>
					<span></span>
					<CustomLink classes='logo-container' url='/'>
						<Logo />
					</CustomLink>
					<div className='header-items'>
						<a
							onClick={hideNav}
							id='close-btn'
							className='quick-link nav-link-text nav-btn js-nav-btn'
							href='#nav'
						>
							<span className='text'>{data.textClose}</span>
							<div className='burger'>
								<span></span>
							</div>
						</a>
					</div>
				</div>
				<div className='key-items'>
					{data.navLinksKey.map((link) => (
						<CustomLink
							classes='key-link js-key-link'
							url={link.url}
							key={`key-item-${link.id}`}
						>
							<span className='text'>{link.text}</span>
							{link.emoji.image !== null && (
								<span
									className={`emoji spin-${link.emoji.spin}`}
								>
									<img
										src={link.emoji.image.url}
										alt={link.emoji.image.alt}
									/>
								</span>
							)}
						</CustomLink>
					))}
				</div>
				<div className='secondary-items'>
					{data.navLinksSecondary.map((link) => (
						<CustomLink
							classes={`secondary-link secondary-link--theme-${link.theme}`}
							url={link.url}
							key={`secondary-item-${link.id}`}
						>
							{link.text}
						</CustomLink>
					))}
				</div>
			</nav>
		</>
	);
};
export default Header;
